/**
 * Component: Free Gift
 * ------------------------------------------------------------------------------
 * Display free gift messaging and products in cart drawer and cart.
 *
 * Offer types:
 * - b2g1f      - Buy 2 Get 1 Free - Buy two products from a collection and get to choose N free gifts
 * - minspend   - Minimum Spend - Spend X in a collection and get to choose N free gifts
 * - collection - Collection - Spend X in a collection and get Y off another collection
 * - bxgy_collection - Buy X Get Y% Off - Buy product X and get Y% off a specific product promoted in popup
 * - bxgy_product - Buy X Get Y - Buy product X and get Y as a free gift in popup
 * @namespace FreeGift
 */
import log from 'salvo-lite/log'
import cssClasses from '../helpers/cssClasses'
import { on } from '../helpers/utils'
import * as cart from '@shopify/theme-cart'

class FreeGift {
    constructor (theme, elem) {
        this._theme = theme
        this._elem = elem
        this._settings = {
            store: window.theme.store.id,
            selectors: {
                messaging: '[free-gift-messaging]',
                sideCartMessaging: '[free-gift-sidecart-message]',
                freeGiftSelectSideCart: '.drawer [free-gift-select]',
                freeGiftSelectCart: '.cart [free-gift-select]',
                sideCartOptionsWrapper: '.drawer [free-gift-select-wrapper]',
                cartOptionsWrapper: '.template-cart__container [free-gift-select-wrapper]',
                popup: '[data-free-gift-popup]',
                popupContent: '.popup__content '
            },
            data: {
                b2g1f1: {},
                b2g1f2: {},
                minspend1: {},
                minspend2: {},
                collection1: {},
                collection2: {},
                bxgy1collection: {},
                bxgy2collection: {},
                bxgy1product: {},
                bxgy2product: {}
            },
            currentProduct: {},
            cart: '',
            isCart: false,
            giftProducts: JSON.parse(document.querySelector('[data-free-gift-products]').getAttribute('data-free-gift-products')),
            ...JSON.parse(elem.getAttribute('data-free-gift') || '{}')
        }

        if (window.SALVO_BINDINGS.template === 'cart') this._settings.isCart = true

        this._nodeSelectors = {
            messaging: document.querySelectorAll(this._settings.selectors.messaging),
            sideCartMessaging: document.querySelector(this._settings.selectors.sideCartMessaging),
            freeGiftSelect: document.querySelectorAll((this._settings.isCart) ? this._settings.selectors.freeGiftSelectCart : this._settings.selectors.freeGiftSelectSideCart),
            optionsWrapper: document.querySelector((this._settings.isCart) ? this._settings.selectors.cartOptionsWrapper : this._settings.selectors.sideCartOptionsWrapper),
            popup: document.querySelector(this._settings.selectors.popup),
            popupContent: document.querySelectorAll(this._settings.selectors.popupContent)
        }

        /**
         * Keeps track of active offers
         */
        this._offers = {
            b2g1f1: {
                type: 'b2g1f',
                minQuantity: null,
                eligibleItemsInCart: 0,
                eligible: false,
                selectedGiftCount: 0,
                freeGiftProducts: [],
                collectionHandle: '',
                active: false
            },
            b2g1f2: {
                type: 'b2g1f',
                minQuantity: null,
                eligibleItemsInCart: 0,
                eligible: false,
                selectedGiftCount: 0,
                freeGiftProducts: [],
                collectionHandle: '',
                active: false
            },
            minspend1: {
                type: 'minspend',
                minSpend: null,
                totalEligibleSpend: 0,
                remainingSpend: 40,
                eligible: false,
                selectedGiftCount: 0,
                freeGiftProducts: [],
                collectionHandle: '',
                active: false
            },
            minspend2: {
                type: 'minspend',
                minSpend: 0,
                totalEligibleSpend: 0,
                remainingSpend: 50,
                eligible: false,
                selectedGiftCount: 0,
                freeGiftProducts: [],
                collectionHandle: '',
                active: false
            },
            collection1: {
                type: 'collection',
                minSpend: 0,
                minQuantity: 0,
                eligibleItemsInCart: 0,
                totalEligibleSpend: 0,
                remainingSpend: 40,
                eligible: false,

                collectionHandle: '',
                collectionUrl: '',
                collectionTitle: '',

                collectionHandleSecond: '',
                collectionUrlSecond: '',
                collectionTitleSecond: '',

                active: false
            },
            collection2: {
                type: 'collection',
                minSpend: 0,
                minQuantity: 0,
                eligibleItemsInCart: 0,
                totalEligibleSpend: 0,
                remainingSpend: 40,
                eligible: false,

                collectionHandle: '',
                collectionUrl: '',
                collectionTitle: '',

                collectionHandleSecond: '',
                collectionUrlSecond: '',
                collectionTitleSecond: '',
                active: false
            },
            bxgy1collection: {
                type: 'bxgy_collection',
                freeGiftProducts: [],
                collectionHandle: '',
                minQuantity: 1,
                eligible: false,
                active: false
            },
            bxgy2collection: {
                type: 'bxgy_collection',
                collectionHandle: '',
                freeGiftProducts: [],
                minQuantity: 1,
                eligible: false,
                active: false
            },
            bxgy1product: {
                type: 'bxgy_product',
                freeGiftProducts: [],
                collectionHandle: '',
                minQuantity: 1,
                message: '',
                eligible: false,
                active: false
            },
            bxgy2product: {
                type: 'bxgy_product',
                collectionHandle: '',
                freeGiftProducts: [],
                minQuantity: 1,
                message: '',
                eligible: false,
                active: false
            }
        }

        log.debug(this.constructor.name, 'Constructed', this)
    }

    /**
     * Initialise component.
     */
    onInit () {
        this._setCurrencySymbol()
        this._addEventListeners()
        this._sortFreeGiftProducts()
        this._setCart()
    }

    /**
     * React to any item changes in the cart.
     */
    _addEventListeners () {
        window.Frame.EventBus.listen(['Toggle:cartDrawerTarget:close'], () => this._toggleDrawerPopup(false))
        window.Frame.EventBus.listen(['Toggle:cartDrawerTarget:open'], () => this._toggleDrawerPopup(true))
        window.Frame.EventBus.listen([
            'AjaxCart:itemAdded', 'AjaxCart:itemRemoved', 'AjaxCart:itemUpdated', 'AjaxCart:cartCleared', 'AjaxCart:update'
        ], () => this._setCart())
    }

    /**
     * Sets cart object on instance
     * @param {object} response
     */
    _setCart () {
        cart.getState().then(state => {
            this._cart = state
            this._setOfferObject()
            this._updateSidecartListHeight()
        })
    }

    /**
     * Set the offer object when the cart is updated and onInit.
    */
    _setOfferObject () {
        if (this._cart.item_count > 0) {
            this._offers['minspend1'].totalEligibleSpend = 0
            this._offers['minspend2'].totalEligibleSpend = 0
            this._offers['collection1'].totalEligibleSpend = 0
            this._offers['collection2'].totalEligibleSpend = 0

            this._cart.items.forEach((cartItem) => {
                Object.keys(this._settings.data).forEach((key) => {
                    const offer = this._offers[key]
                    const data = this._settings.data[key]
                    offer.active = data.active

                    if (offer.type === 'bxgy_product') {
                        offer.qualifyingProductHandle = data.qualifyingProductHandle
                    }

                    if ((offer.type === 'collection' || offer.type === 'minspend') && 'collection' in cartItem.properties) {
                        if (cartItem.properties.collection.includes(offer.collectionHandle)) {
                            offer.totalEligibleSpend += this._formatPrice(cartItem.line_price)
                            this._setRemainingSpend(key)
                        }
                    }
                })
            })
        } else {
            this._handleCartEmpty()
        }

        this._setOfferEligibility()
        this._removeGiftItemsFromCart()
        this._setSelectedGiftCount()
        this._displayMessaging()
        this._displayGiftProductOptions()
        this._displayPopup()
    }

    /**
      * Takes Shopify's cart item line price and returns a 2dp float
      * @param {integer} price
      */
    _formatPrice (price) {
        price = price / 100
        return parseFloat(price.toFixed(2))
    }

    /**
     * Takes the offers key and calculate the remaining amount to spend to be qualify
     * @param {string} key
     */
    _setRemainingSpend (key) {
        const offer = this._offers[key]

        let remaining = parseFloat(offer.minSpend - offer.totalEligibleSpend)

        if (remaining < 0) remaining = 0

        offer.remainingSpend = remaining.toFixed(2)
    }

    /**
     * Update messaging box on product page
     */
    _displayMessaging () {
        let messagingHtml = ''
        let sideCartMessaging = ''

        Object.keys(this._settings.data).forEach((key) => {
            let shopOfferHtml = ''
            let link = ''
            const data = this._settings.data[key]
            const offer = this._offers[key]
            offer.message = data.message

            if (offer.active === true) {
                offer.type === 'bxgy_product' ? link = data.giftProductUrl : link = data.collectionUrl

                if (data.showOfferLink === true) shopOfferHtml = `<a href="${link}">${window.theme.strings.productOffers.shopLink}</a>`

                let offerMessage = window.theme.strings.productOffers.qualified

                if (offer.eligible === false) {
                    if (offer.type === 'minspend') offerMessage = `${window.theme.strings.productOffers.spendMore}. ${shopOfferHtml}`
                    if (offer.type === 'collection' && offer.minSpend > 1) offerMessage = `${window.theme.strings.productOffers.spendMore}. ${shopOfferHtml}`
                    if (offer.type === 'collection' && offer.minSpend === 1 && !offer.minQuantity) offerMessage = `<div class="free-gift__text">${offer.message}. ${shopOfferHtml}</div>`
                    if (offer.type === 'collection' && (offer.minSpend === 1 || offer.minSpend === -1) && offer.minQuantity) offerMessage = `<div>${window.theme.strings.productOffers.addAnother}. ${shopOfferHtml}</div>`
                    if (offer.type === 'b2g1f' || offer.type === 'bxgy_collection') offerMessage = window.theme.strings.productOffers.addAnother
                    if (offer.type === 'bxgy_product') offerMessage = `<div class="free-gift__text">${offer.message}. ${shopOfferHtml}</div>`
                }

                // Replace language placeholders with dynamic values
                offerMessage = offerMessage.replace('#FREEGIFT', `<span>${data.freeGiftName}</span>`)
                offerMessage = offerMessage.replace('#COLLECTION', `<span>${data.collectionTitle}</span>`)
                offerMessage = offerMessage.replace('#VALUE', `<span>${this.currency}${offer.remainingSpend}</span>`)
                offerMessage = offerMessage.replace('#REMAINING', `<span>${offer.minQuantity - offer.eligibleItemsInCart}</span>`)

                sideCartMessaging += `<div class="free-gift__offer" data-free-gift-message="${key}"><div class="free-gift__title">${data.title}</div>${offerMessage}.</div>`
                if (this._settings.currentProduct.collections.includes(data.collectionTitle)) {
                    messagingHtml += `<div class="free-gift__offer" data-free-gift-message="${key}"><div class="free-gift__title">${data.title}</div>${offerMessage}.</div>`
                }

                this._toggleCartOfferMessaging(offer, key)
            }
        })

        if (this._settings.isCart) {
            this._nodeSelectors.messaging.innerHTML = sideCartMessaging
        } else {
            this._nodeSelectors.messaging.forEach((element) => {
                element.innerHTML = `<div class="free-gift__text">${messagingHtml}</div>`
            })
            this._nodeSelectors.sideCartMessaging.innerHTML = `<div class="free-gift__text">${sideCartMessaging}</div>`
            this._updateSidecartListHeight()
        }
    }

    /**
     * Set the height of the cart item list to account for the messaging and gift dropdown
     */
    _updateSidecartListHeight () {
        if (!this._nodeSelectors.sideCartMessaging || !this._nodeSelectors.optionsWrapper) {
            log.warn(this.constructor.name, 'Side cart or option wrapper not found')
            return
        }

        const sideCartMessageHeight = this._nodeSelectors.sideCartMessaging.offsetHeight
        const sideCartGiftProductOptionsHeight = this._nodeSelectors.optionsWrapper.offsetHeight
        const cartDrawer = document.querySelector('.cart-drawer')
        const cartDrawerHeader = document.querySelector('.cart-drawer__header')
        const cartDrawerFooter = document.querySelector('.cart-drawer__footer')
        const klevu = 140

        if (!cartDrawer || !cartDrawerHeader || !cartDrawerFooter) {
            log.warn(this.constructor.name, 'Cart drawer not found')
            return
        }
        const newHeight = cartDrawer.offsetHeight - sideCartMessageHeight - sideCartGiftProductOptionsHeight - cartDrawerHeader.offsetHeight - cartDrawerFooter.offsetHeight - klevu
        document.querySelector('.drawer .cart-drawer__body').style.height = `${newHeight}px`
    }

    /**
     * Takes an offer key and displays free gift congrats message in side cart
     * @param {string} key
     */
    _toggleCartOfferMessaging (offer, key) {
        const preSelector = (this._settings.isCart) ? '.template-cart' : '.drawer'
        const offerMessageBox = document.querySelector(`${preSelector} [data-free-gift-message="${key}"]`)
        const selectBox = document.querySelector(`${preSelector} [data-free-gift-select="${key}"]`)

        if (offerMessageBox) {
            (offer.selectedGiftCount >= 1) ? offerMessageBox.classList.remove(cssClasses.hidden) : offerMessageBox.classList.add(cssClasses.hidden)
        }

        if (selectBox) {
            (offer.selectedGiftCount >= 1) ? selectBox.classList.add(cssClasses.hidden) : selectBox.classList.remove(cssClasses.hidden)
        }
    }

    /**
     * Loop offers and set eligibility boolean
     */
    _setOfferEligibility () {
        Object.keys(this._offers).forEach((key) => {
            const offer = this._offers[key]
            offer.eligible = false

            if (offer.type === 'b2g1f' || offer.type === 'bxgy_collection' || offer.type === 'bxgy_product' || (offer.type === 'collection' && (offer.minSpend === 1 || offer.minSpend === -1))) {
                offer.eligible = this._isTwoCollectionItemsInCart(offer)
            } else {
                if (offer.totalEligibleSpend >= offer.minSpend) offer.eligible = true
            }
        })
    }

    /**
     * Check if there is two items in the cart from the same collection as the B2G1F offer
     * @param {object} offer
     */
    _isTwoCollectionItemsInCart (offer) {
        let eligibleItemCount = 0

        this._cart.items.forEach((item) => {
            if (offer.type === 'bxgy_product') {
                if (offer.qualifyingProductHandle === item.handle) eligibleItemCount += item.quantity
            } else if ('collection' in item.properties) {
                item.properties.collection.forEach((collection) => {
                    if (collection === offer.collectionHandle) eligibleItemCount += item.quantity
                })
            }
        })

        offer.eligibleItemsInCart = eligibleItemCount

        if (eligibleItemCount >= offer.minQuantity) return true
        return false
    }

    /**
     * Reset data if the cart is empty
     */
    _handleCartEmpty () {
        Object.keys(this._offers).forEach((key) => {
            const offer = this._offers[key]
            const data = this._settings.data[key]
            offer.active = data.active

            if (offer.type === 'b2g1f') {
                offer.eligibleItemsInCart = 0
                offer.eligible = false
            } else if (offer.type === 'bxgy_product' || offer.type === 'collection') {
                offer.message = data.message
            } else {
                offer.totalEligibleSpend = 0
                offer.eligible = false
                offer.remainingSpend = offer.minSpend
            }
        })
        this._nodeSelectors.optionsWrapper.innerHTML = ''
    }

    /**
     * Show gift products if eligible, and show active state if they are already in the cart
     */
    _displayGiftProductOptions () {
        let showSelectOptions = false
        let optionsHtml = ''
        const keys = Object.keys(this._offers)
        for (let i = 0; i < keys.length; i++) {
            const offer = this._offers[keys[i]]
            if (offer.eligible === true && offer.active === true) {
                showSelectOptions = true
                break
            }
        }

        if (showSelectOptions === true) {
            keys.forEach((key) => {
                const offer = this._offers[key]
                if (offer.eligible === true && offer.active && offer.type !== 'collection' && offer.type !== 'bxgy_collection' && offer.type !== 'bxgy_product') {
                    optionsHtml += this._loadGiftSelectTemplate(offer, key)
                }
            })
        }

        this._nodeSelectors.optionsWrapper.innerHTML = optionsHtml
        this._addGiftProductSelectListeners()
        this._addGiftProductListeners()
    }

    /**
     * Sort products into their associated offer by tags
     */
    _sortFreeGiftProducts () {
        Object.keys(this._settings.data).forEach((key) => {
            const data = this._settings.data[key]
            const offer = this._offers[key]
            offer.collectionHandle = data.collectionHandle

            if (offer.type === 'b2g1f') {
                offer.minQuantity = parseInt(data.minQuantity)
            }

            if (offer.type === 'minspend' || (offer.type === 'collection' && offer.minSpend !== -1)) {
                offer.minSpend = parseInt(data.minSpend)
                this._setRemainingSpend(key)
            }

            if (offer.type !== 'collection') {
                offer.freeGiftHandles = data.freeGiftHandles.split(',')
                this._settings.giftProducts.forEach((product) => {
                    if (offer.freeGiftHandles.includes(product.handle)) {
                        offer.freeGiftProducts.push(product)
                    }
                })
            }

            if (offer.type === 'collection' && (offer.minSpend === 1 || offer.minSpend === -1)) {
                offer.minQuantity = parseInt(data.minQuantity)
            }
        })
    }

    /**
     * Returns true if a given product exists in the cart
     * @param {object} product
     */
    _isProductInCart (product, key) {
        let isProductInCart = false

        this._cart.items.forEach((item) => {
            if (item.id === parseInt(product.id) && item.properties.relatedOffer === key) isProductInCart = true
        })

        return isProductInCart
    }

    /**
     * Generate HTML for drop down product select box in side cart
     * @param {object} offer
     */
    _loadGiftSelectTemplate (offer, key) {
        let productHtml = ''
        let footerHtml = ''
        let headerHtml = ''
        let classes = ''

        let selectYourText = 'Select your'
        let freeGiftsText = 'free gifts'
        let selectedText = 'Selected'

        if (this._settings.store === 'de_store') {
            selectYourText = 'Wähle Deine'
            freeGiftsText = 'Werbegeschenke'
            selectedText = 'Ausgewählt'
        }

        if (offer.selectedGiftCount > 0) classes = 'product.first_available_variant.id'
        if (this._settings.isCart) {
            headerHtml = `
                <a href="javascript:;" class="free-gift__select-header free-gift__select-header--cart flex f-space-between f-vertical-center">
                    <p class="free-gift__select-title">${selectYourText} <span data-free-gift-limit=""></span> ${freeGiftsText} <span class="free-gift__select-count-text" data-free-gift-header=${key}>${offer.selectedGiftCount}/<span data-free-gift-limit="">1</span> Selected</span></p>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="8" viewBox="0 0 16 8"><g><g transform="rotate(90 8 4)"><path fill="#008200" d="M4.136-3.157a.5.5 0 0 1 .728-.686l7 7.44a.5.5 0 0 1 .003.682l-7 7.56a.5.5 0 0 1-.734-.679l6.683-7.218z"></path></g></g></svg>
                </a>`
        } else {
            headerHtml = `
                <a href="javascript:;" class="free-gift__select-header flex f-space-between f-vertical-center">
                    <p class="free-gift__select-title">${selectYourText} <span data-free-gift-limit=""></span> ${freeGiftsText}</p>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="8" viewBox="0 0 16 8"><g><g transform="rotate(90 8 4)"><path fill="#008200" d="M4.136-3.157a.5.5 0 0 1 .728-.686l7 7.44a.5.5 0 0 1 .003.682l-7 7.56a.5.5 0 0 1-.734-.679l6.683-7.218z"></path></g></g></svg>
                </a>`

            footerHtml = `
                <div class="free-gift__select-footer" data-free-gift-footer=${key}>
                    <p class="free-gift__select-count-text">${offer.selectedGiftCount}/<span data-free-gift-limit="">1</span> ${selectedText}</p>
                </div>`
        }

        offer.freeGiftProducts.forEach((product) => {
            const isProductInCart = this._isProductInCart(product, key)
            const selectClasses = (isProductInCart === true) ? 'active' : ''
            const setAttributes = (isProductInCart === true) ? 'checked' : ''

            productHtml += `
            <div class="free-gift__product">
                <div class="free-gift__checkbox flex"><label class="flex" for="checkbox-${product.id}-${key}"><input type="checkbox" ${setAttributes} data-free-gift-id="${product.id}" data-related-offer=${key} id="checkbox-${product.id}-${key}"/><span></span>Select Gift</label></div>
                <div class="flex f-space-between">
                <div class="free-gift__product-content flex f-vertical-center ">
                    <img class="free-gift__product-image" src="${product.featured_image}" alt="${product.title} Featured Image">
                    <div>
                        <h3 class="free-gift__product-title">${product.title}</h3>
                        <p class="free-gift__product-sku">Part No. ${product.sku}</p>
                    </div>
                </div>
                <a class="free-gift__product-select ${selectClasses}" data-free-gift-id="${product.id}" data-related-offer=${key} href="javascript:;">Select</a>
                </div>
            </div>
            `
        })

        return `
        <div class="free-gift__select ${classes}" free-gift-select data-free-gift-select="${key}">
            ${headerHtml}
            <div class="free-gift__select-options">
                ${productHtml}
            </div>
            ${footerHtml}
        </div>
        `
    }

    /**
     * Add event listeners to product select dropdown
     */
    _addGiftProductSelectListeners () {
        document.querySelectorAll('[free-gift-select]').forEach((select) => {
            const selectElement = select.querySelector('.free-gift__select-header')
            on('click', selectElement, (e) => {
                select.classList.toggle('active')
                this._updateSidecartListHeight()
            })
        })
    }

    /**
     * Sets the number of gifts selected on the offer object
     */
    _setSelectedGiftCount () {
        Object.keys(this._offers).forEach((key) => {
            const offer = this._offers[key]
            let selectedGiftCount = 0
            this._cart.items.forEach((item) => {
                if ('relatedOffer' in item.properties) {
                    if (item.properties.relatedOffer === key) {
                        selectedGiftCount++
                    }
                }
            })
            offer.selectedGiftCount = selectedGiftCount
        })
    }

    /**
      * Takes the offer key and increments the gift count total
      * @param {string} key
      */
    _updateSelectCount (offer, key) {
        const selector = (this._settings.isCart) ? `[data-free-gift-header=${key}]` : `[data-free-gift-footer="${key}"]`
        offer.selectedGiftCount++

        if (this._settings.isCart) {
            document.querySelector(selector).innerHTML = offer.selectedGiftCount
        } else {
            document.querySelector(selector).innerHTML = `
            <p class="free-gift__select-count-text">${offer.selectedGiftCount}/<span data-free-gift-limit="">${offer.minQuantity}</span> Selected</p>
            `
        }

        this._toggleCartOfferMessaging(offer, key)
    }

    /**
     * Add event listeners to the gift product select
     */
    _addGiftProductListeners () {
        const selectButtons = document.querySelectorAll('.free-gift__product-select')
        const selectCheckboxes = document.querySelectorAll('.free-gift__checkbox input')

        selectButtons.forEach((button) => {
            on('click', button, (e) => {
                const element = e.target
                if (element.classList.contains('active')) return false
                const id = parseInt(e.target.getAttribute('data-free-gift-id'))
                const key = e.target.getAttribute('data-related-offer')
                this._addGiftToCart(element, id, key)
            })
        })

        selectCheckboxes.forEach((checkbox) => {
            on('change', checkbox, (e) => {
                const element = e.target.parentNode
                element.classList.add('active')
                const id = parseInt(e.target.getAttribute('data-free-gift-id'))
                const key = e.target.getAttribute('data-related-offer')
                this._addGiftToCart(element, id, key)
            })
        })
    }

    /**
     * Add gift to cart
     * @param {element} element
     * @param {integer} id
     * @param {string} key
     */
    _addGiftToCart (element, id, key) {
        const offer = this._offers[key]
        if (offer.selectedGiftCount >= 1) return false

        cart.addItem(id, { quantity: 1, properties: { 'relatedOffer': key } })
            .then(response => {
                element.classList.add('active')
                window.Frame.EventBus.emit('AjaxCart:itemAdded')
                if (offer.type !== 'bxgy_product' && offer.type !== 'bxgy_collection') this._updateSelectCount(offer, key)
                this._toggleCartOfferMessaging(offer, key)
            })
    }

    /**
     * Removes free gifts from the cart if user is no longer eligible
     */
    _removeGiftItemsFromCart () {
        const itemKeys = []

        this._cart.items.forEach((cartItem) => {
            if ('relatedOffer' in cartItem.properties) {
                const offer = this._offers[cartItem.properties.relatedOffer]

                if (!offer.eligible && offer.selectedGiftCount > 0) itemKeys.push(cartItem.key)
            }
        })

        itemKeys.forEach(key => {
            cart.updateItem(key, { quantity: 0 })
                .then(() => window.Frame.EventBus.emit('AjaxCart:itemAdded'))
        })
    }

    /**
     * Get currency symbol
    */
    _setCurrencySymbol () {
        switch (window.Shopify.currency.active) {
        case 'GBP':
            this.currency = '£'
            break
        case 'EUR':
            this.currency = '€'
            break
        }
    }

    _toggleDrawerPopup (show) {
        if (show) {
            this._nodeSelectors.popup.classList.remove(cssClasses.hidden)
        } else {
            this._nodeSelectors.popup.classList.add(cssClasses.hidden)
        }
    }

    _displayPopup () {
        this._nodeSelectors.popupContent.forEach((content) => {
            const contentKey = content.getAttribute('data-free-gift-popup-content')
            content.classList.add(cssClasses.hidden)

            Object.keys(this._settings.data).forEach((key) => {
                const offer = this._offers[key]
                if (key === contentKey && offer.active && !offer.eligible && offer.minSpend !== -1 && offer.minSpend !== 1) {
                    content.classList.remove(cssClasses.hidden)
                }
            })
        })
    }
}

export default FreeGift



// WEBPACK FOOTER //
// ./src/resources/scripts/components/FreeGift.js